import { useEffect, useState } from 'react'

import { UpsellingPopover as F1UpsellingPopover } from '@factorialco/f0-react'
import { AvailableModules, Features } from 'generated/featureDefinitions'

import { track } from 'modules/core/lib/Tracking'
import { trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellGuard } from 'modules/upselling_kit/components/UpsellGuard'
import { useCreateUpsellRequestWithErrorHandling } from 'modules/upselling_kit/hooks/useCreateUpsellRequest'
import { useUpsell } from 'modules/upselling_kit/lib/upsellContext'
import { UpsellingKitModule } from 'modules/upselling_kit/types'
import { useDefaultMessages } from 'modules/upselling_kit/utils/defaultMessages'
import { handleUpsellingVisibility } from 'modules/upselling_kit/utils/handlers'

type Side = 'top' | 'bottom' | 'left' | 'right'

type ProductWidgetPopoverBaseProps = {
  label?: string
  mediaUrl: string
  requestSource: string
  moduleRequested: AvailableModules
  requestReason: ValidReasons
  featureRequested?: Features
  pageSource?: string
  variant: 'outlinePromote' | 'outline' | 'ghost'
  title?: string
  description?: string
  size?: 'sm' | 'md' | 'lg'
  hideLabel?: boolean
  side?: Side
}

type StandaloneProps = ProductWidgetPopoverBaseProps & UpsellingKitModule

const ProductWidgetPopoverContent = ({
  mediaUrl,
  label,
  variant,
  title,
  description,
  size = 'md',
  hideLabel,
  side,
}: {
  mediaUrl: string
  label?: string
  variant: 'outlinePromote' | 'outline' | 'ghost'
  title?: string
  description?: string
  size?: 'sm' | 'md' | 'lg'
  hideLabel?: boolean
  side?: Side
}) => {
  const [isOpen, toggleOpen] = useState(false)
  const { create } = useCreateUpsellRequestWithErrorHandling()
  const { requestSource, moduleRequested, upsellData } = useUpsell()
  const defaultMessages = useDefaultMessages()

  const handleRequest = async () => {
    track(trackingEvents.upsellingKit.clickProductUpsellComponent, {
      source: requestSource,
      component: 'upselling_popover',
      module: moduleRequested,
    })
    await create({
      moduleRequested,
      requestSource,
      requestReason: ValidReasons.MissingAddon,
      upsellRequestComponentType: 'upselling_popover',
    })
  }

  useEffect(() => {
    handleUpsellingVisibility({ moduleRequested, requestSource, component: 'upselling_popover' })
  }, [moduleRequested, requestSource])

  useEffect(() => {
    if (isOpen) {
      track(trackingEvents.upsellingKit.viewedProductUpsellComponent, {
        source: requestSource,
        component: 'upselling_popover',
        module: moduleRequested,
      })
    }
  }, [isOpen, requestSource, moduleRequested])

  return (
    <F1UpsellingPopover
      isOpen={isOpen}
      setIsOpen={toggleOpen}
      side={side}
      label={label || ''}
      hideLabel={hideLabel}
      variant={variant}
      mediaUrl={mediaUrl}
      size={size}
      title={title || upsellData.upsellModalTitle || ''}
      description={description || upsellData.upsellModalDescription || ''}
      actions={[
        {
          type: 'upsell',
          label: i18n.t('upselling_kit.button.request_information'),
          variant: 'promote',
          onClick: handleRequest,
          errorMessage: defaultMessages.error,
          successMessage: defaultMessages.success,
          loadingState: defaultMessages.loading,
          nextSteps: defaultMessages.nextSteps,
          closeLabel: defaultMessages.close,
          showConfirmation: true,
        },
      ]}
    ></F1UpsellingPopover>
  )
}

export const ProductWidgetPopover = ({
  mediaUrl,
  label,
  variant,
  title,
  description,
  fallbackComponent,
  size,
  hideLabel,
  side,
  ...upsellProps
}: StandaloneProps) => {
  return (
    <UpsellGuard {...upsellProps} fallbackComponent={fallbackComponent || null}>
      <ProductWidgetPopoverContent
        mediaUrl={mediaUrl}
        label={label}
        variant={variant}
        title={title}
        description={description}
        size={size}
        hideLabel={hideLabel}
        side={side}
      />
    </UpsellGuard>
  )
}
